/**
 * Tracking Data Extraction Utilities
 *
 * Helper functions for extracting Google Ads tracking parameters
 * and other tracking identifiers from URL and cookies.
 */

/**
 * Extract Google Click ID (gclid) from URL query string
 *
 * Google Ads adds gclid parameter to URLs for conversion tracking.
 * Example: ?gclid=TeSteR1234567890
 *
 * @returns gclid value or undefined if not present
 */
export function getGclid(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search)
    const gclid = params.get('gclid')

    // Return gclid if it exists and is not empty
    return gclid && gclid.trim().length > 0 ? gclid.trim() : undefined
  } catch (error) {
    console.warn('Failed to extract gclid from URL:', error)
    return undefined
  }
}

/**
 * Extract Google Client ID (gcid) from _gcl_aw cookie
 *
 * Google Ads stores conversion linker data in the _gcl_aw cookie.
 * Cookie format: _gcl_aw=GCL.{timestamp}.{gcid}
 * Example: _gcl_aw=GCL.1234567890.ABC123XYZ
 *
 * @returns gcid value or undefined if cookie not found or parsing fails
 */
export function getGcid(): string | undefined {
  try {
    // Get all cookies
    const cookies = document.cookie.split(';')

    // Find _gcl_aw cookie
    const gclAwCookie = cookies.find(cookie =>
      cookie.trim().startsWith('_gcl_aw=')
    )

    if (!gclAwCookie) {
      return undefined
    }

    // Extract cookie value (everything after '=')
    const cookieValue = gclAwCookie.split('=')[1]

    if (!cookieValue || cookieValue.trim().length === 0) {
      return undefined
    }

    // Parse cookie value: GCL.timestamp.gcid
    // Split by '.' and get the third part (gcid)
    const parts = cookieValue.trim().split('.')

    // Cookie should have at least 3 parts: GCL, timestamp, gcid
    if (parts.length >= 3) {
      const gcid = parts[2]
      return gcid && gcid.length > 0 ? gcid : undefined
    }

    return undefined
  } catch (error) {
    console.warn('Failed to extract gcid from _gcl_aw cookie:', error)
    return undefined
  }
}

/**
 * Clean session ID by removing "sess_" prefix
 *
 * Converts internal session ID format to external transaction ID format.
 * Example: "sess_abc123xyz" -> "abc123xyz"
 *
 * @param sessionId Session ID with or without "sess_" prefix
 * @returns Session ID without prefix (external_transaction_id)
 */
export function cleanSessionId(sessionId: string): string {
  if (!sessionId) {
    return ''
  }

  return sessionId.replace(/^sess_/, '').trim()
}

/**
 * Extract all tracking parameters in one call
 *
 * Convenience function to get all tracking identifiers at once.
 *
 * @param sessionId Current session ID
 * @returns Object with all tracking parameters
 */
export function getAllTrackingParams(sessionId: string): {
  gclid?: string
  gcid?: string
  external_transaction_id: string
} {
  return {
    gclid: getGclid(),
    gcid: getGcid(),
    external_transaction_id: cleanSessionId(sessionId)
  }
}
